import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from "@chakra-ui/react";
import { formatAmountInINR } from "jsFunctions/formatAmountInINR";
import dateTimeFormat from "jsFunctions/dateTimeFormat";
import { useDispatch, useSelector } from "react-redux";
import "./paymentModePdf.css";
import { IoClose } from "react-icons/io5";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { FaFileDownload } from "react-icons/fa";
import { fetchAsyncGetVendorByID } from "redux/vendorSlice";
import { formatAmountIndianRupees } from "jsFunctions/formatAmountInINR";
import { convertAmountToWords } from "./helper";
import SpinnerLoader from "components/Spinner/SpinnerLoader";
import { useToasts } from "react-toast-notifications";

import GetAddress from "components/GetAddress";
import { calculateTotalCredit } from "jsFunctions/helperFunctions";
import { poNumberFormate } from "jsFunctions/helperFunctions";
import { printPaymentAdvicePdf } from "api/expense";
import { GetCountries,GetState,GetCity, } from 'react-country-state-city';
import moment from "moment";

const ShowAddress = ({address})=>{
  if (!address) return {};
  const state = address.stateName;
  const city = address.cityName;
  const country = address.countryName;
  return (
    <>
      {address?.s1 && <p>{address?.s1}</p>}
      {address?.s2 && <p>{address?.s2}</p>}
      <p>{city ? `${city},` : ""}{state}</p>
      <p>{country ? country : ""}{address?.pin ? `,${address?.pin}` : ""}</p>
    </>
  )
}

const PaymentModePdf = (props) => {
  const { isOpen, onClose, expenseData, paymentData, autoCount, expenseType } = props;
  const organizationList = useSelector((state) => state.organizationData);
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const [vendorDetail, setVendorDetail] = useState(null);
  const [reciverName, setReciverName] = useState("");
  const [loading, setLoading] = useState(true);
  const logoData = useSelector((state) => state.logoData);
  const logoUrl = logoData?.downloadUrl || "";
  const [addresses,setAddresses] = useState();
  const [addressLoading,setAddressLoading] = useState(true);
  
  const fetchVendorDetails = useCallback(
    async (vendorId) => {
      setLoading(true);
      try {
        const res = await dispatch(fetchAsyncGetVendorByID(vendorId));
        if (res?.payload) {
          setVendorDetail(res.payload.vendor);
        }
      } catch (error) {
        addToast(`Error fetching vendor details: ${error.message}`, {
          appearance: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [dispatch, addToast]
  );

  useEffect(() => {
    if (expenseData) {
      const vendorId = expenseData?.resId?._id || expenseData?.mrId?._id;
      if (vendorId) {
        fetchVendorDetails(vendorId);
      }
      else{
        setReciverName(expenseData?.rcId?.lab)
      }
    }
  }, [expenseData, fetchVendorDetails]);

  const gstData = useMemo(() => {
    return expenseData?.gstApl?.gstId || [];
  }, [expenseData]);

  const tdsData = useMemo(() => {
    if (expenseData?.tdsApl?.sts) {
      return expenseData.tdsApl?.list?.map((item) => ({
        totalAmount: item?.bsAmt,
        tdsPercent: item?.prc,
        totalTDS: item?.tlAmt,
      }));
    } else if (expenseType === "tds") {
      return {
        totalAmount: expenseData?.tlBlAmt,
        tdsPercent: expenseData?.prc,
        totalTDS: expenseData?.tdsAmt,
      };
    }
    return null;
  }, [expenseData, expenseType]);

  const historyData = useMemo(
    () =>
      expenseData?.pyInfo?.list
        ?.map?.((p, i) =>
          i === autoCount
            ? {
                amt: Number(paymentData?.paymentAmount) || 0,
                refNo: paymentData?.refNo,
                pmtDt: paymentData?.selectedDate,
              }
            : p,
        )
        ?.reverse?.(),
    [expenseData?.pyInfo?.list, paymentData, autoCount],
  );

  const organizationData = useMemo(() => {
    if (organizationList?.length) {
      return organizationList[0];
    }
  }, [organizationList]);

  const totalPaymentAmount = useMemo(() => {
    return paymentData?.paymentAmount || 0;
  }, [paymentData]);

  const totalGst = useMemo(() => {
    return gstData.reduce((total, item) => {
      const gstAmount = item?._id.amt || {};
      return (
        total +
        (parseFloat(gstAmount?.s || 0) +
          parseFloat(gstAmount?.i || 0) +
          parseFloat(gstAmount?.c || 0))
      );
    }, 0);
  }, [gstData]);

  const totalTDS = useMemo(() => {
    return tdsData?.reduce((total, item) => total + (item.totalTDS || 0), 0);
  }, [tdsData]);

  const totalPaymentHistory = useMemo(() => {
    return historyData?.reduce((total, item) => total + (item?.amt || 0), 0);
  }, [historyData]);

  const creditAmt = useMemo(() => {
    if(expenseData?.crApl?.sts && expenseData?.crApl?.list?.length){
      const amount = calculateTotalCredit(expenseData?.crApl?.list);
      return amount
    }
    return 0;
  }, [expenseData?.crApl]);

  const creditData = useMemo(() => {
    if (expenseData?.crApl?.list) {
      return expenseData.crApl.list;
    }
    return [];
  }, [expenseData]);

  const fetchAndSetAddresses=useCallback(async()=> {
    if (loading) return;
    setAddressLoading(true);
    try {
      const [orgAddress, vendorAddress] = await fetchAddressDetails(organizationData?.add, vendorDetail?.blAd);
      setAddresses({orgAddress, vendorAddress});
    } finally {
      setAddressLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    fetchAndSetAddresses()
  }, [fetchAndSetAddresses]);

  const fetchAddressDetails = async (...addresses) => {
    const cache = {
      countries: null,
      states: {},
      cities: {},
    };

    cache.countries = await GetCountries() || [];

    const results = await Promise.all(
      addresses.map(async (address) => {
        if (!address) return {}
        const countryId = address?.ctr || 101;
        const stateId = address.st || null;
        const cityId = address.ct || null;

        const country =
          cache.countries.find(c => c.id === countryId);

        if (!cache.states[countryId]) {
          cache.states[countryId] = GetState(countryId) || [];
        }
        const state =
          (await cache.states[countryId]).find(s => s.id === stateId);

        const cityKey = `${countryId}_${stateId}`;
        if (stateId && !cache.cities[cityKey]) {
          cache.cities[cityKey] = GetCity(countryId, stateId) || [];
        }
        const city =
          (await cache.cities[cityKey])?.find(c => c.id === cityId);

        return {
          ...address,
          countryName: country?.name || "",
          stateName: state?.name || "",
          cityName: city?.name || ""
        };
      })
    );
  
    return results;
  };

  const handlePdf = async () => {
    try {
      const formatAmountInINR = (amount) => {
        return '₹ ' + formatAmountIndianRupees(amount);
      }
      const {orgAddress, vendorAddress} = addresses || {};
      const paymentAdvicePayload = {
        logoUrl,
    
        organization: {
          ...orgAddress,
          name: organizationData?.name || "",
          address: organizationData?.add || {},
          cpId: organizationData?.cpId || "",
          gstNo: organizationData?.gstNo || "",
        },
    
        vendor: {
          name: vendorDetail?.name || reciverName || "",
          address: {
            ...vendorAddress,
            ...vendorDetail?.blAd,
          },
          gstNo: vendorDetail?.gsts?.[0]?.gstNo || "NA",
          pos: vendorDetail?.gsts?.[0]?.pos || "",
        },
    
        payment: {
          id: paymentData?.paymentId || "",
          date: moment(paymentData?.selectedDate).format("DD-MMM-yyyy"),
          refNo: paymentData?.refNo || "-",
          bank:
            paymentData?.bankValue?.label ||
            paymentData?.bankValue ||
            "-",
          amount: formatAmountIndianRupees(totalPaymentAmount),
          amountInWords: convertAmountToWords(totalPaymentAmount),
          amountFormatted: formatAmountInINR(totalPaymentAmount),
        },
    
        expense: {
          billNumber:
            expenseData?.pyInfo?.blNo ||
            paymentData?.billNumber ||
            "-",
          orderDate: moment(expenseData?.oDt).format("DD-MMM-yyyy"),
          orderAmount: formatAmountInINR(expenseData?.pyInfo?.tlAmt || 0),
        },
    
        totals: {
          gst: formatAmountInINR(totalGst),
          tds: formatAmountInINR(totalTDS),
          credit: formatAmountInINR(creditAmt),
          paymentHistory: formatAmountInINR(totalPaymentHistory),
        },
    
        gstData: (gstData || []).map((item) => {
          const { bsAmt, prc, amt } = item?._id || {};
          const total =
            (parseFloat(amt?.s || 0) +
              parseFloat(amt?.i || 0) +
              parseFloat(amt?.c || 0));
    
          return {
            taxable: formatAmountInINR(Number(bsAmt || 0)),
            percent: prc || 0,
            igst: formatAmountIndianRupees(Number(amt?.i || 0)),
            sgst: formatAmountIndianRupees(Number(amt?.s || 0)),
            cgst: formatAmountIndianRupees(Number(amt?.c || 0)),
            total: formatAmountIndianRupees(Number(total || 0)),
          };
        }),
    
        tdsData: (tdsData || []).map((item) => ({
          taxable: formatAmountInINR(item?.totalAmount || 0),
          percent: item?.tdsPercent || 0,
          amount: formatAmountInINR(item?.totalTDS || 0),
        })),
    
        creditData: (creditData || []).map((item) => ({
          crNo: poNumberFormate(item?.crId?.crNo || ""),
          amount: formatAmountInINR(item?.crId?.amt || 0),
          used: formatAmountInINR(item?.usAmt || 0),
        })),
    
        historyData: (historyData || []).map((item) => ({
          billNumber:
            expenseData?.pyInfo?.blNo ||
            item?.blNo ||
            "-",
          orderDate: moment(expenseData?.oDt).format("DD-MMM-yyyy"),
          orderAmount: formatAmountInINR(expenseData?.pyInfo?.tlAmt || 0),
          paymentDate: moment(item?.pmtDt).format("DD-MMM-yyyy"),
          amount: formatAmountInINR(item?.amt || 0),
        })),
    
        show: {
          gst: gstData?.length > 0,
          tds: tdsData?.length > 0,
          credit: creditData?.length > 0,
          history: historyData?.length > 0,
        },
      };
      await printPaymentAdvicePdf(paymentAdvicePayload, paymentData?.paymentId && `${paymentData?.paymentId}.pdf`);
    } catch (error) {
      console.error(error);
      addToast('Error downloading document', {appearance: 'error'});
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="modal-main" size="xl">
      <ModalOverlay />
      <ModalContent
        className="add-form-modal-content"
        top="0"
        margin="0 auto"
        bottom="0"
      >
        <div className="fixed-header  mt-0">
          <Box className="payment-template">
            <Tooltip
              label="Close"
              placement="bottom"
              hasArrow
              className="common-tooltip"
            >
              <button className="close-btn" onClick={onClose}>
                <IoClose />
              </button>
            </Tooltip>
            <Tooltip
              label="Download"
              placement="bottom"
              hasArrow
              className="common-tooltip"
            >
              <button onClick={handlePdf} className="pdf-downlode-icon">
                <FaFileDownload />
              </button>
            </Tooltip>
          </Box>
        </div>
        <ModalBody>
          <>
            <Box className="payment-mode-details">
              {addressLoading ? (
                <SpinnerLoader />
              ) : (
                <>
                  <Box id="payment-mode" className="main-template">
                    <div className="details-container">
                      <Box className="ledger-template">
                        <Flex
                          align="center"
                          direction="column"
                          className="ledger-logo"
                        >
                          <img src={logoUrl} alt="logo" />
                        </Flex>
                        <Box
                          display="flex"
                          className="ledger-header"
                          justifyContent="space-between"
                        >
                          <div className="header-left header-box">
                            <Text className="title mt-3">
                              {organizationData?.name}
                            </Text>
                            <Text className="address">
                              <ShowAddress address={addresses?.orgAddress} />
                            </Text>
                            <Text className="address">
                            <span>Company ID :</span>
                            <span> {organizationData?.cpId}</span>
                          </Text>
                          <Text className="address">
                            <span> GST :</span>
                            <span> {organizationData?.gstNo}</span>
                          </Text>
                         
                          </div>
                          <div className="header-right header-box">
                            <Text className="label paid-to">Paid To</Text>

                            <Text className="title">{vendorDetail?.name || reciverName}</Text>
                            <Text className="address">
                              <ShowAddress address={addresses?.vendorAddress} />
                            </Text>
                            <Text className="address">
                            <span>GST :</span>
                            <span> {vendorDetail?.gsts[0]?.gstNo ? vendorDetail?.gsts[0]?.gstNo : 'NA'}</span>
                          </Text>
                          </div>
                        </Box>
                      </Box>
                    </div>
                    <Box className="payment-mads">
                      <Text>
                        <strong>Payment Advice</strong>
                      </Text>
                    </Box>
                    <Box>
                      <Box className="payment-details">
                        <div className="payment-text-div">
                          <div className="text1">Payment#</div>
                          <div className="text2">{paymentData?.paymentId}</div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Payment Date</div>
                          <div className="text2">
                            {dateTimeFormat(paymentData?.selectedDate)}
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Reference Number</div>
                          <div className="text2">
                            {paymentData?.refNo ? paymentData?.refNo : "-"}
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Paid To</div>
                          <div className="text2">{vendorDetail?.name}</div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Place Of Supply</div>
                          <div className="text2">
                            {vendorDetail?.gsts && vendorDetail?.gsts?.length
                              ? `${vendorDetail?.gsts[0]?.pos} (${vendorDetail?.gsts[0]?.gstNo})`
                              : "-"}
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Bank Name</div>
                          <div className="text2">
                            {paymentData?.bankValue
                              ? paymentData?.bankValue?.label ||
                                paymentData?.bankValue
                              : "-"}
                          </div>
                        </div>
                        
                        <div className="payment-text-div">
                          <div className="text1">Amount Paid</div>
                          <div className="text2">
                            ₹{" "}
                            {formatAmountIndianRupees(
                              paymentData?.paymentAmount || 0
                            )}
                            /-
                          </div>
                        </div>
                        <div className="payment-text-div">
                          <div className="text1">Amount Paid In Words</div>
                          <div className="text2">
                            Indian Rupee{" "}
                            {convertAmountToWords(
                              paymentData?.paymentAmount || 0
                            )}{" "}
                            
                          </div>
                        </div>
                      </Box>
                      <Box className="amount-div">
                        <span>Total Amount Paid</span>
                        <br />
                        <span className="pcs-total">
                          {formatAmountInINR(totalPaymentHistory || 0)}
                        </span>
                      </Box>
                    </Box>
                    <div className="clear"></div>
                   
                  </Box>
                  <Box id="payment-for" className="main-template">
                    <div className="payment-table-box mt-5">
                      <h4 className="heading">Payment Information</h4>
                      <Table>
                        <Thead>
                          <Tr>
                            <Th className="width_150">Bill Number</Th>
                            <Th>Order Date</Th>
                            <Th>Order Amount</Th>
                            <Th>Payment Date</Th>
                            <Th className="width_150">Payment Amount</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          <Tr>
                            <Td>
                              {
                              expenseData?.pyInfo?.blNo ? 
                              expenseData?.pyInfo?.blNo
                              :
                              paymentData?.billNumber
                                ? paymentData?.billNumber
                                : "-"}
                            </Td>
                            <Td>{dateTimeFormat(expenseData?.oDt)}</Td>
                            <Td>
                              {formatAmountInINR(
                                expenseData?.pyInfo?.tlAmt || 0
                              )}
                            </Td>
                            <Td>{dateTimeFormat(paymentData?.selectedDate)}</Td>
                            <Td>
                              {formatAmountInINR(
                                paymentData?.paymentAmount || 0
                              )}
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </div>
                    {gstData && gstData?.length > 0 && (
                      <div className="payment-table-box">
                        <h4 className="heading">GST Information</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              {/* <Th>No.</Th> */}
                              <Th className="width_150">Taxable Amount</Th>
                              <Th>GST</Th>
                              <Th>IGST</Th>
                              <Th>SGST</Th>
                              <Th>CGST</Th>
                              <Th>Total GST</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {gstData.map((item, index) => {
                              const { bsAmt, prc, amt } = item?._id;
                              const totalGst =
                                parseFloat(amt?.s || 0) +
                                parseFloat(amt?.i || 0) +
                                parseFloat(amt?.c || 0);
                              return (
                                <Tr key={index}>
                                  <Td>
                                    {bsAmt
                                      ? formatAmountInINR(Number(bsAmt || 0))
                                      : Number(0).toFixed(2)}
                                  </Td>

                                  <Td>{prc || 0} %</Td>

                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {amt?.i
                                      ? formatAmountIndianRupees(Number(amt?.i))
                                      : Number(0).toFixed(2)}
                                  </Td>
                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {amt?.s
                                      ? formatAmountIndianRupees(Number(amt?.s))
                                      : Number(0).toFixed(2)}
                                  </Td>
                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {amt?.c
                                      ? formatAmountIndianRupees(Number(amt?.c))
                                      : Number(0).toFixed(2)}
                                  </Td>
                                  <Td>
                                    <span className="rupee-symbol">₹</span>{" "}
                                    {totalGst
                                      ? formatAmountIndianRupees(
                                          Number(totalGst)
                                        )
                                      : Number(0).toFixed(2)}
                                  </Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={5}>
                                Total GST:
                              </Td>
                              <Td className="f-600">
                                {" "}
                                {formatAmountInINR(totalGst)}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    )}
                    {tdsData && tdsData?.length > 0 && (
                      <div className="payment-table-box">
                        <h4 className="heading">TDS Information</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>Taxable Amount</Th>
                              <Th>TDS</Th>
                              <Th>TDS Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {tdsData.map((item, index) => {
                              return (
                                <Tr>
                                  <Td>
                                    {formatAmountInINR(item?.totalAmount || 0)}
                                  </Td>
                                  <Td>{item?.tdsPercent || 0} %</Td>
                                  <Td>
                                    {formatAmountInINR(item?.totalTDS || 0)}
                                  </Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={2}>
                                Total TDS:
                              </Td>
                              <Td className="f-600">
                                {" "}
                                {formatAmountInINR(totalTDS)}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    )}
                    {creditData && creditData?.length ? (
                      <div className="payment-table-box">
                        <h4 className="heading">Credit Information</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>CR No.</Th>
                              <Th>Amount</Th>
                              <Th>Use Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {creditData.map((item, index) => {
                              return (
                                <Tr>
                                  <Td>{poNumberFormate(item?.crId.crNo)}</Td>
                                  <Td>
                                    {formatAmountInINR(item?.crId?.amt || 0)}
                                  </Td>
                                  <Td>
                                    {formatAmountInINR(item?.usAmt || 0)}
                                  </Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={2}>
                                Total Credit:
                              </Td>
                              <Td className="f-600">
                                {" "}
                                {formatAmountInINR(creditAmt)}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    ):""}
                    {historyData && historyData?.length > 0 && (
                      <div className="payment-table-box">
                        <h4 className="heading">Payment History</h4>
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>Bill Number</Th>
                              <Th>Order Date</Th>
                              <Th>Order Amount</Th>
                              <Th>Payment Date</Th>
                              <Th>Payment Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {historyData?.map((item) => {
                              return (
                                <Tr>
                                  <Td>{expenseData?.pyInfo?.blNo ? expenseData?.pyInfo?.blNo : item?.blNo ? item?.blNo : "-"}</Td>
                                  <Td>{dateTimeFormat(expenseData?.oDt)}</Td>
                                  <Td>
                                    {formatAmountInINR(
                                      expenseData?.pyInfo?.tlAmt || 0
                                    )}
                                  </Td>
                                  <Td>{dateTimeFormat(item?.pmtDt)}</Td>
                                  <Td>{formatAmountInINR(item?.amt || 0)}</Td>
                                </Tr>
                              );
                            })}
                            <Tr>
                              <Td className="f-600" colSpan={4}>
                                Total Payment:
                              </Td>
                              <Td className="f-600">
                                {" "}
                                {formatAmountInINR(totalPaymentHistory)}
                              </Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </div>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PaymentModePdf;
